<template>
  <div class="stall-shop-info">
    <!-- 顶部导航 -->
    <header class="header">
      <button class="btn-icon" @click="goBack">←</button>
      <div class="header-center">{{ $t('user.shopInfo') }}</div>
      <button class="btn-save" :disabled="saving" @click="handleSave">
        {{ saving ? $t('shopInfo.saving') : $t('common.save') }}
      </button>
    </header>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner">⏳</div>
      <p>{{ $t('common.loading') }}</p>
    </div>

    <!-- 表单内容 -->
    <div v-else class="form-container">
      <!-- 店铺头像 -->
      <div class="form-section">
        <div class="section-title">📷 {{ $t('shopInfo.avatar') }}</div>
        <div class="avatar-wrapper">
          <div class="avatar-box" @click="triggerAvatarUpload">
            <img v-if="formData.shopAvatar" :src="getImageUrl(formData.shopAvatar)" :alt="$t('shopInfo.avatar')" @error="handleImageError" />
            <div v-else class="avatar-placeholder">
              <span>📷</span>
              <p>{{ $t('shopInfo.uploadClick') }}</p>
            </div>
          </div>
          <input ref="avatarInput" type="file" accept="image/*" style="display: none;" @change="handleAvatarUpload" />
        </div>
      </div>

      <!-- 基本信息 -->
      <div class="form-section">
        <div class="section-title">📝 {{ $t('shopInfo.basicInfo') }}</div>
        
        <div class="form-item">
          <label>{{ $t('user.customerName') }}</label>
          <input type="text" v-model="formData.customerName" :placeholder="$t('shopInfo.namePlaceholder')" maxlength="50" />
        </div>

        <div class="form-item">
          <label>{{ $t('shopInfo.desc') }}</label>
          <textarea v-model="formData.shopDesc" :placeholder="$t('shopInfo.descPlaceholder')" rows="3" maxlength="200"></textarea>
        </div>

        <div class="form-item">
          <label>{{ $t('user.contact') }}</label>
          <input type="text" v-model="formData.contactPerson" :placeholder="$t('shopInfo.contactPlaceholder')" maxlength="30" />
        </div>

        <div class="form-item">
          <label>{{ $t('delivery.contactPhone') }}</label>
          <input type="tel" v-model="formData.phone" :placeholder="$t('shopInfo.phonePlaceholder')" maxlength="20" />
        </div>

        <div class="form-item">
          <label>{{ $t('shopInfo.wechat') }}</label>
          <input type="text" v-model="formData.wechat" :placeholder="$t('shopInfo.wechatPlaceholder')" maxlength="50" />
        </div>

        <div class="form-item">
          <label>💬 WhatsApp</label>
          <input type="text" v-model="formData.whatsapp" placeholder="例如：65 12345678（含国家代码）" maxlength="50" />
        </div>

        <div class="form-item">
          <label>店铺{{ $t('user.address') }}</label>
          <input type="text" v-model="formData.companyAddress" :placeholder="$t('shopInfo.addressPlaceholder')" maxlength="200" />
        </div>

        <div class="form-item">
          <label>{{ $t('Stall.StallShopInfo.line70_text') }}</label>
          <input type="text" v-model="formData.taxNo" :placeholder="$t('Stall.StallShopInfo.line71_placeholder')" maxlength="100" />
        </div>

        <div class="form-item">
          <label>{{ $t('common.remark') }}</label>
          <input type="text" v-model="formData.remark" :placeholder="$t('shopInfo.remarkPlaceholder')" maxlength="200" />
        </div>
      </div>

      <!-- 只读信息 -->
      <div class="form-section readonly-section">
        <div class="section-title">ℹ️ {{ $t('shopInfo.systemInfo') }}</div>
        
        <div class="info-row">
          <span class="info-label">{{ $t('shopInfo.customerCode') }}</span>
          <span class="info-value">{{ readOnlyFields.customerCode || '-' }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">{{ $t('shopInfo.region') }}</span>
          <span class="info-value">{{ regionDisplay }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { getImageUrl } from '@/utils/image';
import { uploadFile } from '@/services/api';

export default {
  name: 'StallShopInfo',
  data() {
    return {
      loading: true,
      saving: false,
      customerId: null,
      formData: {
        customerName: '',
        shopDesc: '',
        shopAvatar: '',
        contactPerson: '',
        phone: '',
        wechat: '',
        whatsapp: '',
        companyAddress: '',
        remark: ''
      },
      // 只读字段
      readOnlyFields: {
        customerCode: '',
        regionCode: '',
        regionName: ''
      },
      // 保存管理后台其他字段（更新时回传避免被覆盖）
      adminFields: {},
      originalData: {}
    };
  },
  async mounted() {
    await this.loadCustomerInfo();
  },
  computed: {
    regionDisplay() {
      if (this.readOnlyFields.regionName) return this.readOnlyFields.regionName;
      if (this.readOnlyFields.regionCode) return this.readOnlyFields.regionCode;
      return '-';
    }
  },
  methods: {
    goBack() {
      this.$router.push('/stall/profile');
    },

    async loadCustomerInfo() {
      try {
        // ⭐ 从后端获取当前用户信息（不依赖登录时的 localStorage）
        const meRes = await api.get('/auth/me');
        const me = meRes.data || meRes;
        this.customerId = me.customerId || null;

        if (!this.customerId) {
          // 兼容旧数据：从登录时保存的档口 ID 获取
          this.customerId = localStorage.getItem('stored_customer_id');
        }

        if (!this.customerId) {
          // 兼容旧数据：从 userInfo 获取
          const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
          this.customerId = userInfo.customerId || userInfo.identityId;
        }

        if (!this.customerId) {
          this.$message.error('无法获取档口信息');
          this.loading = false;
          return;
        }

        const res = await api.get(`/customers/${this.customerId}`);
        const customer = res.data || res;

        // 档口可编辑的字段
        this.formData = {
          customerName: customer.customerName || '',
          shopDesc: customer.shopDesc || '',
          shopAvatar: customer.shopAvatar || '',
          contactPerson: customer.contactPerson || '',
          phone: customer.phone || '',
          wechat: customer.wechat || '',
          whatsapp: customer.whatsapp || '',
          companyAddress: customer.companyAddress || '',
          remark: customer.remark || '',
          taxNo: customer.taxNo || ''
        };

        // 只读字段
        this.readOnlyFields = {
          customerCode: customer.customerCode || '',
          regionCode: customer.regionCode || '',
          regionName: customer.regionName || ''
        };

        // ⭐ 保存管理后台其他字段（更新时回传避免被覆盖）
        this.adminFields = {
          customerName: customer.customerName || '',
          customerType: customer.customerType,
          status: customer.status,
          authStatus: customer.authStatus,
          shopLevel: customer.shopLevel,
          isVip: customer.isVip
        };

        this.originalData = { ...this.formData };
      } catch (error) {
        console.error('加载店铺信息失败:', error);
        this.$message.error('加载失败');
      } finally {
        this.loading = false;
      }
    },

    triggerAvatarUpload() {
      this.$refs.avatarInput.click();
    },

    async handleAvatarUpload(e) {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        this.$message.warning('图片大小不能超过 5MB');
        return;
      }

      try {
        const result = await uploadFile('/files/upload-image', file);
        this.formData.shopAvatar = result.url || result.data?.url;
      } catch (error) {
        console.error('上传头像失败:', error);
        this.$message.error('上传失败');
      }
      // 重置 input
      e.target.value = '';
    },

    handleImageError(e) {
      console.warn('头像加载失败:', e.target.src);
      this.formData.shopAvatar = '';
    },

    async handleSave() {
      if (!this.formData.customerName?.trim()) {
        this.$message.warning('请输入店铺名称');
        return;
      }

      this.saving = true;
      try {
        // 只发送可编辑字段，不要发送空值覆盖后端数据
        const submitData = {
          customerName: this.formData.customerName.trim(),
          shopDesc: this.formData.shopDesc?.trim() || '',
          shopAvatar: this.formData.shopAvatar || '',
          contactPerson: this.formData.contactPerson?.trim() || '',
          phone: this.formData.phone?.trim() || '',
          wechat: this.formData.wechat?.trim() || '',
          whatsapp: this.formData.whatsapp?.trim() || '',
          companyAddress: this.formData.companyAddress?.trim() || '',
          remark: this.formData.remark?.trim() || '',
          taxNo: this.formData.taxNo?.trim() || '',
          // 保留只读字段
          regionCode: this.readOnlyFields.regionCode || '',
          regionName: this.readOnlyFields.regionName || '',
          customerCode: this.readOnlyFields.customerCode || '',
          // 保留管理后台字段
          customerType: this.adminFields.customerType || 'stall',
          status: this.adminFields.status || 1,
          authStatus: this.adminFields.authStatus || 0,
          shopLevel: this.adminFields.shopLevel || 1,
          isVip: this.adminFields.isVip || 0
        };

        await api.put(`/customers/${this.customerId}`, submitData);

        // 更新 localStorage 中的信息
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        userInfo.customerName = submitData.customerName;
        localStorage.setItem('userInfo', JSON.stringify(userInfo));

        this.$message.success(this.$t('toast.saveSuccess'));
        this.originalData = { ...this.formData };
        this.goBack();
      } catch (error) {
        console.error('Failed to save shop info:', error);
        this.$message.error(this.$t('toast.saveFailed') + ': ' + (error.response?.data?.message || error.message || ''));
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.stall-shop-info {
  min-height: 100vh;
  background: #f5f5f5;
}

/* 顶部导航 */
.header {
  background: white;
  border-bottom: 1px solid #eee;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-center {
  flex: 1;
  text-align: center;
  font-weight: bold;
  color: #333;
  font-size: 16px;
}

.btn-icon {
  font-size: 20px;
  cursor: pointer;
  background: none;
  border: none;
  color: #333;
  padding: 4px 8px;
}

.btn-save {
  font-size: 14px;
  font-weight: bold;
  color: #667eea;
  background: none;
  border: none;
  padding: 6px 12px;
  cursor: pointer;
}

.btn-save:disabled {
  color: #999;
  cursor: not-allowed;
}

/* 加载状态 */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.loading-spinner {
  font-size: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 表单容器 */
.form-container {
  padding-bottom: 20px;
}

/* 表单区块 */
.form-section {
  background: white;
  margin: 12px;
  border-radius: 12px;
  padding: 16px;
}

.section-title {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

/* 头像上传 */
.avatar-wrapper {
  display: flex;
  justify-content: center;
  padding: 10px 0;
}

.avatar-box {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #ddd;
  transition: border-color 0.3s;
}

.avatar-box:hover {
  border-color: #667eea;
}

.avatar-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #999;
}

.avatar-placeholder span {
  font-size: 28px;
}

.avatar-placeholder p {
  font-size: 11px;
  margin: 4px 0 0;
}

/* 表单项 */
.form-item {
  margin-bottom: 16px;
}

.form-item:last-child {
  margin-bottom: 0;
}

.form-item label {
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
}

.form-item input,
.form-item textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  background: #fafafa;
  transition: border-color 0.3s, background 0.3s;
  box-sizing: border-box;
}

.form-item input:focus,
.form-item textarea:focus {
  outline: none;
  border-color: #667eea;
  background: white;
}

.form-item input::placeholder,
.form-item textarea::placeholder {
  color: #bbb;
}

.form-item textarea {
  resize: vertical;
}

/* 只读信息区块 */
.readonly-section {
  opacity: 0.85;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 13px;
  color: #999;
}

.info-value {
  font-size: 13px;
  color: #333;
  text-align: right;
  max-width: 60%;
  word-break: break-all;
}
</style>
